import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import useAxios from '../../services/AxiosConfig';
import { resetFilterState } from '../../slicers/FilterSlice';
import managementStyles from '../ManagementPages.module.css';
import PortalFilter from './components/PortalFilter';
import VitalSummaryCard from './components/VitalSummaryCard';
import styles from './InpatientPortal.module.css';
import InpatientDetail from './InpatientDetail';
import InpatientAlarmSettingsModal from './components/InpatientAlarmSettingsModal';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { getAlertSilenceTimestamp } from '../../utils/alertUtils';
import { setUpdating, setFormValue, resetModalities } from './UpdatePatchSlice';
import { getInitialStartTime } from '../../utils/programUtils';
import { convertAndFormatMeasurementValue } from '../../utils/unitUtils';
import PerinHealthModal from '../../components/PerinHealthModel';
import { openModal, closeModal } from '../../components/PerinHealthModelSlice';
import InpatientAlarmsListModal from './components/InpatientAlarmsListModal';

const InpatientPortal = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.userInfo);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const { dateFormat, unitType } = useSelector((state) => state.userInfo);
    const [patientLocation, setPatientLocation] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [alarmsListModalOpen, setAlarmsListModalOpen] = useState(false);
    const [alarmsListModalPatient, setAlarmsListModalPatient] = useState(null);
    const [settingsModal, setSettingsModal] = useState({
        open: false,
        alert: {}
    });
    const patientReadingsRef = useRef({});
    const [resolveConfirmationModal, setResolveConfirmationModal] = useState({
        open: false,
        alert: null
    });
    const [silenceConfirmationModal, setSilenceConfirmationModal] = useState({
        open: false,
        alert: null,
        action: '',
        duration: null,
        durationDisplay: ''
    });

    const navigate = useNavigate();

    const processReadings = (readingsResponses) => {
        return readingsResponses.reduce((acc, patientData) => {
            const patientId = patientData.accountId;

            if (patientData.readings?.length > 0) {
                const groupedReadings = patientData.readings.reduce((group, reading) => {
                    if (!group[reading.modalityId]) {
                        group[reading.modalityId] = [];
                    }
                    group[reading.modalityId].push(reading);
                    return group;
                }, {});

                const patientReadings = Object.entries(groupedReadings).reduce((readings, [modalityId, modalityReadings]) => {
                    const deviceOneReading = modalityReadings.find(r => r.deviceId === 1);
                    const selectedReading = deviceOneReading || modalityReadings[0];

                    readings[modalityId] = {
                        ...selectedReading,
                    };

                    return readings;
                }, {});

                acc[patientId] = {
                    data: patientReadings
                };
            }

            return acc;
        }, {});
    };

    const formatAlerts = (patientIds, alertsData) => {
        const grouped = alertsData.reduce((acc, alert) => {
            const id = alert.alert_accountId;
            if (!acc[id]) {
                acc[id] = [];
            }
            acc[id].push(alert);
            return acc;
        }, {});

        const alertsMap = {};
        for (const id of patientIds) {
            alertsMap[id] = grouped[id] || [];
        }
        return alertsMap;
    };

    const getVitalsFromReadings = (patientId, readings, userUnitPreference) => {
        const patientData = readings[patientId];
        if (!patientData || !patientData.data) {
            return {
                heartRate: { value: "--", state: "Default" },
                respiratoryRate: { value: "--", state: "Default" },
                oxygenSaturation: { value: "--", state: "Default" },
                temperature: { value: "--", state: "Default" }
            };
        }

        const readingData = patientData.data;

        // Helper to get measurement value and state
        const getMeasurement = (modalityId) => {
            const reading = readingData[modalityId];
            if (!reading || reading.measurement === null || reading.measurement === undefined) {
                return { value: "--", state: "Default" };
            }

            let measurementVal = reading.measurement;
            if (Array.isArray(measurementVal)) {
                if (Array.isArray(measurementVal[0])) {
                    measurementVal = (measurementVal.reduce((total, item) => total + item[0], 0)) / measurementVal.length;
                } else {
                    measurementVal = (measurementVal.reduce((total, item) => total + item, 0)) / measurementVal.length;
                }
            }

            const formattedValue = convertAndFormatMeasurementValue(measurementVal, modalityId, userUnitPreference);

            // Determine state based on background_color
            let state = "Default";
            if (reading.background_color === "yellow") {
                state = "alert-medium";
            } else if (reading.background_color === "red") {
                state = "alert-high";
            } else if (reading.background_color === "green") {
                state = "Default";
            }
            
            return {
                value: formattedValue,
                modalityId: reading.modalityId,
                state: state,
                completedDate: reading.completedDate || null,
                background_color: reading.background_color || null,
                readingAccuracy: reading.SQI_reading_accuracy || null
            };
        };

        return {
            heartRate: getMeasurement(4), 
            respiratoryRate: getMeasurement(6), 
            oxygenSaturation: getMeasurement(3), 
            temperature: getMeasurement(1) 
        };
    };

    // Helper function to determine overall patient state
    const determinePatientState = (vitals, alerts, batteryLevel) => {
        // Check for high priority alerts first
        const hasHighAlert = alerts.some(alert => alert.alert_priority === 'high');
        if (hasHighAlert) {
            return 'alert-high';
        }

        const hasMediumAlert = alerts.some(alert => alert.alert_priority === 'medium');
        if (hasMediumAlert) {
            return 'alert-medium';
        }

        const hasLowAlert = alerts.some(alert => alert.alert_priority === 'low');
        if (hasLowAlert) {
            return 'alert-low';
        }

        // Check battery level
        if (batteryLevel === null && batteryLevel === undefined) {
            return 'inactive';
        }

        return 'Default';
    };

    useEffect(() => {
        getPatientLocation();
    }, []);

    const getPatientLocation = async () => {
        dispatch(resetFilterState());
        try {
            let patientsWithActiveEnrollments = [];
            const responseData = await axios.get(`patient/ehr/list/${user.id}`);
            if (Array.isArray(responseData.data.data)) {
                const filteredData = responseData.data.data.filter(item => item.activelyEnrolledFlg);
                patientsWithActiveEnrollments = filteredData.map(item => {
                    return {
                        fullName: `${item.firstName} ${item.lastName}`,
                        encounterId: item.patientLocation.encounterId,
                        mrn: item.patientLocation.mrn,
                        location: item.patientLocation.location,
                        floor: item.patientLocation.floor,
                        room: item.patientLocation.room,
                        bed: item.patientLocation.bed,
                        phone: item.patientLocation.phone,
                        email: item.patientLocation.email,
                        perinUserId: item.patientLocation.perinUserId,
                        organizationId: item.patientLocation.organizationId,
                        ...item
                    };
                });
            } else {
                console.error('Invalid data format:');
            }


            const patientIds = patientsWithActiveEnrollments.map(item => item.id);

            if (patientIds.length === 0) {
                setPatientLocation([]);
                return;
            }

            const batteryLevelsPromises = patientIds.map(async (patientId) => {
                try {
                    const response = await axios.get(`measurements/patch/metadata/latest/${patientId}`);
                    if ((response.data?.success || response.data?.code === 200) && response.data?.data) {
                        const batteryLevel = response.data.data.batteryLevel;
                        return { patientId, batteryLevel: batteryLevel ? Math.round(batteryLevel * 100) : null };
                    }
                    return { patientId, batteryLevel: null };
                } catch (err) {
                    return { patientId, batteryLevel: null };
                }
            });

            const enrollmentsPromises = patientIds.map(async (patientId) => {
                try {
                    const response = await axios.get(`enrollment/patient/active/${patientId}`);
                    if ((response.data?.success || response.data?.code === 200) && response.data?.data) {
                        const enrollment = response.data.data;
                        return { patientId, enrollment: enrollment };
                    }
                    return { patientId, enrollment: null };
                } catch (err) {
                    return { patientId, enrollment: null };
                }
            });

            const [
                patientReadingsResponse,
                alertResponse,
                batteryLevelsArray,
                enrollmentsArray
            ] = await Promise.all([
                axios.get(`device/readings/latestByIds?accountIds=${patientIds.join(',')}`),
                axios.post(`alerts/list`, { accountIds: patientIds }),
                Promise.all(batteryLevelsPromises),
                Promise.all(enrollmentsPromises)
            ]);

            // Process battery levels
            const batteryLevelsMap = batteryLevelsArray.reduce((acc, { patientId, batteryLevel }) => {
                acc[patientId] = batteryLevel;
                return acc;
            }, {});

            const enrollmentsMap = enrollmentsArray.reduce((acc, { patientId, enrollment }) => {
                acc[patientId] = enrollment;
                return acc;
            }, {});

            // Process readings
            const readings = processReadings(patientReadingsResponse?.data?.data || []);
            patientReadingsRef.current = readings;

            // Process alerts
            const formattedAlerts = formatAlerts(patientIds, alertResponse?.data?.data ?? []);

            // Format patient data with real readings and battery levels
            const formattedData = patientsWithActiveEnrollments.map((item) => {
                const vitals = getVitalsFromReadings(item.id, readings, unitType);
                let batteryLevel = batteryLevelsMap[item.id];
                if (batteryLevel === undefined) {
                    batteryLevel = null;
                }
                const enrollment = enrollmentsMap[item.id] ?? null;
                // Determine overall state based on vitals and alerts
                const state = determinePatientState(vitals, formattedAlerts[item.id] || [], batteryLevel);

                // Find the most recent completedDate from all vitals
                const allCompletedDates = Object.values(vitals)
                    .map(v => v.completedDate)
                    .filter(date => date !== null && date !== undefined);
                
                const lastRecordedTimestamp = allCompletedDates.length > 0
                    ? allCompletedDates.reduce((latest, current) => {
                        return new Date(current) > new Date(latest) ? current : latest;
                    })
                    : null;

                return {
                    ...item,
                    alerts: formattedAlerts[item.id] || [],
                    vitals,
                    state,
                    status: 'Stationary',
                    deviceId: '1234567890',
                    batteryLevel,
                    enrollment,
                    lastRecordedTimestamp
                };
            });

            setPatientLocation(formattedData);
        } catch (err) {
            console.error('Error fetching patient locations:', err);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Failed to fetch patient locations",
                    type: 'danger',
                })
            );
        }
    };

    const onPatientClick = (patientData) => {
        const patient = typeof patientData === 'object' ? patientData : patientLocation.find(p => p.fullName === patientData);
        setSelectedPatient(patient);
    };

    const handleBack = () => {
        setSelectedPatient(null);
    };

    const handleAlarmSettingsClose = () => {
        setSettingsModal({
            open: false,
            alert: {}
        });
    };

    const handleResolveConfirmation = async () => {
        const alert = resolveConfirmationModal.alert;
        if (!alert) return;

        // Close the confirmation modal
        setResolveConfirmationModal({
            open: false,
            alert: null
        });

        // Resolve the alert
        const resolved = await submitAlertAction(alert, 'clear');
        
        if (resolved) {
            dispatch(showAlert({
                header: 'Success',
                message: 'Alarm resolved successfully',
                type: 'success',
                dismissable: false
            }));
            
            // Close any open modals
            setAlarmsListModalOpen(false);
            setAlarmsListModalPatient(null);
            handleAlarmSettingsClose();
            
            // Refresh patient data
            getPatientLocation();
        } else {
            dispatch(showAlert({
                header: 'Error',
                message: 'Failed to resolve alarm',
                type: 'danger',
                dismissable: false
            }));
        }
    };

    const handleResolveConfirmationClose = () => {
        setResolveConfirmationModal({
            open: false,
            alert: null
        });
    };

    const handleSilenceConfirmation = async () => {
        const alert = silenceConfirmationModal.alert;
        const action = silenceConfirmationModal.action;
        const duration = silenceConfirmationModal.duration;
        const durationDisplay = silenceConfirmationModal.durationDisplay;
        
        if (!alert) return;

        // Close the confirmation modal
        setSilenceConfirmationModal({
            open: false,
            alert: null,
            action: '',
            duration: null,
            durationDisplay: ''
        });

        // Submit the silence/unsilence action
        const success = await submitAlertAction(alert, action, duration);
        
        if (success) {
            const actionMessage = action === 'silence' 
                ? `Alarm silenced for ${durationDisplay}`
                : 'Alarm unsilenced';
            
            dispatch(showAlert({
                header: 'Success',
                message: actionMessage,
                type: 'success',
                dismissable: false
            }));
            
            // Close any open modals
            setAlarmsListModalOpen(false);
            setAlarmsListModalPatient(null);
            handleAlarmSettingsClose();
            
            // Refresh patient data
            getPatientLocation();
        } else {
            dispatch(showAlert({
                header: 'Error',
                message: `Failed to ${action} alarm`,
                type: 'danger',
                dismissable: false
            }));
        }
    };

    const handleSilenceConfirmationClose = () => {
        setSilenceConfirmationModal({
            open: false,
            alert: null,
            action: '',
            duration: null,
            durationDisplay: ''
        });
    };

    const handleAlertAction = async (alert, action, duration = null, durationDisplay = "") => {
        
        switch (action) {
            case 'silence':
                // Show confirmation modal
                setSilenceConfirmationModal({
                    open: true,
                    alert: alert,
                    action: 'silence',
                    duration: duration,
                    durationDisplay: durationDisplay
                });
                break;
            case 'unsilence':
                // Show confirmation modal
                setSilenceConfirmationModal({
                    open: true,
                    alert: alert,
                    action: 'unsilence',
                    duration: null,
                    durationDisplay: ''
                });
                break;
            case 'resolve':
                setSettingsModal({
                    open: false,
                    alert: {}
                });
                // Show confirmation modal
                setResolveConfirmationModal({
                    open: true,
                    alert: alert
                });
                break;
            case 'report':
                dispatch(showAlert({
                    header: 'Report Submitted',
                    message: 'Technical issue reported',
                    type: 'success',
                    dismissable: false
                }));
                break;
            case 'updateEnrollment':
                handleUpdateCareProgram(alert);
                break;
            default:
                break;
        }
    };

    const sortPatientsByPriority = (patients) => {
        const priorityOrder = {
            'alert-high': 1,
            'alert-medium': 2,
            'Default': 3,
            'inactive': 4,
            'empty-battery': 4
        };
        
        return [...patients].sort((a, b) => {
            const priorityA = priorityOrder[a.state] || 3;
            const priorityB = priorityOrder[b.state] || 3;
            return priorityA - priorityB;
        });
    };


    const submitAlertAction = async (alert, action, duration = null) => {
        try {
            const baseAlertActions = {
                report: { L: [] },
                clearAlert: { L: [] },
                silenceAlert: { L: [] },
                escalate: { L: [] },
                document: { L: [] }
            };

            // First, preserve all existing actions
            if (alert.alert_actions) {
                Object.keys(alert.alert_actions).forEach(actionType => {
                    const existingActions = alert.alert_actions[actionType];
                    if (existingActions && existingActions.L) {
                        // If it's already in DynamoDB format with L property
                        baseAlertActions[actionType].L = [...existingActions.L];
                    } else if (Array.isArray(existingActions)) {
                        // If it's a plain array, convert to DynamoDB format
                        // Filter out any undefined or null values
                        baseAlertActions[actionType].L = existingActions
                            .filter(action => action && action.timestamp && action.userId)
                            .map(action => ({
                                M: {
                                    timestamp: { S: action.timestamp },
                                    userId: { N: action.userId.toString() },
                                    ...(action.duration && { duration: { N: action.duration.toString() } })
                                }
                            }));
                    }
                });
            }

            // Map the action to the correct key in alert_actions
            const actionMap = {
                'clear': 'clearAlert',
                'silence': 'silenceAlert',
                'unsilence': 'silenceAlert',
            };

            const status = action === 'clear' ? 'resolved' : 'active';

            // Create the action data in DynamoDB format
            const actionData = {
                M: {
                    timestamp: { S: new Date().toISOString() },
                    userId: { N: user.id.toString() }
                }
            };

            // Add duration if it exists (for silence action)
            if (duration) {
                actionData.M.duration = { N: duration.toString() };
            }

            // Add the new action to the list
            const actionKey = actionMap[action];
            if (actionKey && baseAlertActions[actionKey]) {
                baseAlertActions[actionKey].L.push(actionData);
            }

            const alertData = {
                accountId: parseInt(alert.alert_accountId),
                careTeam_accountId: user.id,
                alertId: alert.alertId,
                createdDate: alert.createdDate,
                status: status,
                alert_actions: baseAlertActions
            };

            if (duration) {
                alertData['silenceTimestamp'] = getAlertSilenceTimestamp(duration);
            }

            if (action === 'unsilence') {
                // Change to now
                alertData['silenceTimestamp'] = getAlertSilenceTimestamp(0);
            }

            const response = await axios.request({
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                url: '/alerts',
                data: JSON.stringify(alertData)
            });

            return true;
        } catch (error) {
            console.error("Error updating alert:", error);
            return false;
        }
    };

    const handleAction = async (action, patient) => {
        switch (action) {
            case 'acknowledge':
                // Update patient state to remove alert
                const success = await submitAlertAction(patient.alerts[0], 'clear');
                if (success) {
                    dispatch(showAlert({
                        header: 'Success',
                        message: 'Alarm Acknowledged',
                        type: 'success',
                        dismissable: false
                    }));
                    getPatientLocation();
                } else {
                    dispatch(showAlert({
                        header: 'Error',
                        message: 'Failed to acknowledge alarm',
                        type: 'danger',
                        dismissable: false
                    }));
                }
                break;
            case 'details':
                // Open alarm settings modal
                const alert = patient.alerts[0];
                setSettingsModal({
                    open: true,
                    alert: alert
                });
                break;
            case 'listAlarms':
                setAlarmsListModalPatient(patient);
                setAlarmsListModalOpen(true);
                break;
            case 'viewVitals':
                onPatientClick(patient);
                break;
            case 'discharge':
                // Show confirmation modal
                setSelectedPatient(patient);
                dispatch(
                    openModal({
                        activeModalId: "InpatientPortal",
                        header: 'Discharge Patient?',
                        message: `Are you sure you want to discharge this patient? <br /><span style="color: #dc3545; font-weight: bold;">This action cannot be undone.</span>`,
                        buttons: [
                            { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                            { label: "Confirm", variant: 'primary', onClickKey: 'YES-DISCHARGE' },
                        ],
                    })
                );
                break;
            case 'updateEnrollment':
                handleUpdateCareProgram(patient);
                break;
            default:
                break;
        }
    };

    const handleDischarge = () => {
        setSelectedPatient(null);
        getPatientLocation();
    };

    const handleUpdateCareProgram = (alertOrPatient) => {
        // Determine if we received an alert or patient object
        const isAlert = alertOrPatient && (alertOrPatient.alertId || alertOrPatient.alert_name);
        let targetPatient;
        
        if (isAlert) {
            // Find patient from patientLocation using alert info
            targetPatient = patientLocation.find(p => 
                p.id === alertOrPatient.patientId || 
                p.fullName === alertOrPatient.patientName
            );
            
            if (alertOrPatient.patientName) {
                dispatch(setUpdating(alertOrPatient.patientName));
            }
        } else {
            // We received a patient object directly
            targetPatient = alertOrPatient;
            
            if (targetPatient && targetPatient.fullName) {
                dispatch(setUpdating(targetPatient.fullName));
            }
        }
        
        // Set accountId if patient found
        if (targetPatient && targetPatient.id) {
            dispatch(setFormValue({ name: "accountId", value: targetPatient.id }));
        }
        
        axios.get(`device/group/${user.id}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const deviceData = responseData.data.data;
                    const patchDevice = deviceData.find(device => device.deviceId === 1);
                    dispatch(setFormValue({ name: "patchInformation", value: patchDevice }));
                    
                    // If patient has enrollment with devices, set the device from enrollment
                    if (targetPatient && targetPatient.enrollment && targetPatient.enrollment[0] && targetPatient.enrollment[0].devices) {
                        const enrollmentDevices = targetPatient.enrollment[0].devices;
                        const enrollmentPatchDevices = enrollmentDevices.filter(device => device.deviceId === 1);
                        dispatch(setFormValue({ name: "program", value: targetPatient.enrollment[0] }));
                        
                        if (enrollmentPatchDevices.length > 0) {
                            // Use the first patch device for the main device object (for serial number, etc.)
                            const firstPatchDevice = enrollmentPatchDevices[0];
                            dispatch(setFormValue({ name: "device", value: firstPatchDevice }));
                            dispatch(setFormValue({ name: "modalities", value: enrollmentPatchDevices }));
                        } else {
                            dispatch(resetModalities());
                        }
                    } else {
                        dispatch(resetModalities());
                    }
                    
                    navigate('/update-patch');
                } else {
                    console.error('Invalid data format:');
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }

    const handleButtonClick = (onClickKey) => {
        if (onClickKey === 'YES-DISCHARGE') {
            dischargeHandler();
        } else if (onClickKey === 'YES-RESOLVE') {

        } else if (onClickKey === 'YES-CLEAR-ALL') {
            dispatch(closeModal());
            clearAllAlarms();
        } else {
            dispatch(closeModal());
        }
    };

    const dischargeHandler = async () => {
        dispatch(closeModal());
        try {
            const response = await axios.delete(`enrollment/inpatient/${selectedPatient.enrollment[0].enrollmentId}/${selectedPatient.id}`);
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Patient has been discharged",
                    type: 'success'
                })
            );
            setSelectedPatient(null);
            getPatientLocation();
        } catch (error) {
            console.error('Error discharging patient:', error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "There was an error discharging the patient",
                    type: 'danger'
                })
            );
        }
    };

    const clearAllAlarms = async () => {
        const patient = alarmsListModalPatient;
        if (!patient || !patient.alerts) return;
        
        const alerts = patient.alerts;
        let successCount = 0;
        let failureCount = 0;
        
        // Resolve all alerts sequentially
        for (const alert of alerts) {
            const success = await submitAlertAction(alert, 'clear');
            if (success) {
                successCount++;
            } else {
                failureCount++;
            }
        }
        
        // Show appropriate alert based on results
        if (failureCount === 0) {
            dispatch(showAlert({
                header: 'Success',
                message: `Successfully cleared ${successCount} alarm${successCount !== 1 ? 's' : ''}`,
                type: 'success',
                dismissable: false
            }));
        } else if (successCount === 0) {
            dispatch(showAlert({
                header: 'Error',
                message: `Failed to clear ${failureCount} alarm${failureCount !== 1 ? 's' : ''}`,
                type: 'danger',
                dismissable: false
            }));
        } else {
            dispatch(showAlert({
                header: 'Partial Success',
                message: `Cleared ${successCount} alarm${successCount !== 1 ? 's' : ''}, but failed to clear ${failureCount} alarm${failureCount !== 1 ? 's' : ''}`,
                type: 'warning',
                dismissable: false
            }));
        }
        
        setAlarmsListModalOpen(false);
        setAlarmsListModalPatient(null);
        handleAlarmSettingsClose();
        getPatientLocation();
    };

    return (
        <div className={styles.InpatientPortalContent}>
            <PortalFilter deviceCount={patientLocation.length} />
            {selectedPatient ? 
                <InpatientDetail patient={selectedPatient} onBack={handleBack} onDischarge={handleDischarge} /> :
                <div className={managementStyles.GridContainer}>
                    <div className={styles.CardGrid}>
                        {sortPatientsByPriority(patientLocation).map((patient) => (
                            <VitalSummaryCard
                                key={patient.id}
                                vitals={patient.vitals}
                                batteryLevel={patient.batteryLevel}
                                lastRecordedTimestamp={patient.lastRecordedTimestamp}
                                dateFormat={dateFormat}
                                onPatientClick={onPatientClick}
                                patient={patient}
                                onAction={handleAction}
                            />
                        ))}
                    </div>
                </div>
            }
            <InpatientAlarmSettingsModal
                settingsModal={settingsModal}
                onClose={handleAlarmSettingsClose}
                handleAlertAction={handleAlertAction}
            />

            <InpatientAlarmsListModal
                show={alarmsListModalOpen}
                onHide={() => {
                    setAlarmsListModalOpen(false);
                    setAlarmsListModalPatient(null);
                }}
                alerts={alarmsListModalPatient?.alerts || []}
                patientName={alarmsListModalPatient?.fullName || ''}
                onAcknowledgeAll={() => {
                    if (alarmsListModalPatient) {
                        // Close the alarms list modal
                        setAlarmsListModalOpen(false);
                        // Show confirmation modal
                        dispatch(
                            openModal({
                                activeModalId: "InpatientPortal",
                                header: 'Clear All Alarms?',
                                message: `Are you sure you want to clear all alarms for ${alarmsListModalPatient.fullName}? <br /><span style="color: #dc3545; font-weight: bold;">This action cannot be undone.</span>`,
                                buttons: [
                                    { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                                    { label: "Confirm", variant: 'primary', onClickKey: 'YES-CLEAR-ALL' },
                                ],
                            })
                        );
                    }
                }}
                onAlarmSettings={(alert) => {
                    setAlarmsListModalOpen(false);
                    setSettingsModal({
                        open: true,
                        alert: alert
                    });
                }}
            />

            <PerinHealthModal  
                    modalId={"InpatientPortal"}
                    handleButtonClick={handleButtonClick}
                />  
            
            {/* Resolve Confirmation Modal */}
            <Modal 
                show={resolveConfirmationModal.open} 
                onHide={handleResolveConfirmationClose} 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Resolve Alarm?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to resolve this alarm?</p>
                    <p><u>This action cannot be undone</u></p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleResolveConfirmationClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleResolveConfirmation}
                        style={{ background: '#9F1919', border: 'none' }}
                    >
                        Resolve
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Silence Confirmation Modal */}
            <Modal 
                show={silenceConfirmationModal.open} 
                onHide={handleSilenceConfirmationClose} 
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {silenceConfirmationModal.action === 'silence' ? 'Silence Alarm' : 'Unsilence Alarm'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {silenceConfirmationModal.action === 'silence' 
                            ? `Are you sure you want to silence this alarm for ${silenceConfirmationModal.durationDisplay}?`
                            : 'Are you sure you want to unsilence this alarm?'}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleSilenceConfirmationClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSilenceConfirmation}
                        style={{ background: '#1D5769', border: 'none' }}
                    >
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default InpatientPortal;
